import { Injectable } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { PaymentsSettings } from '../../../settings-data/sub-models/company-settings/payments.model';
import { IRendererOptions } from '../../../renderer/renderer.interface';
import { Map } from 'immutable';
import { Document } from '../../../document-data/document.model';
import {
  IAddPaymentPayload,
  IMarkAsPaidData,
  ISendReceiptPreview,
} from '../../../payments-data/payments-data.data';
import { DateTimeService } from 'i2g-ng-utils';

@Injectable()
export class MarkAsPaidDialogBuilder {
  FORM_KEYS = {
    PAYMENT_METHODS_RADIO: 'methodsRadio',
    CUSTOM_METHOD_RADIO: 'customMethodRadio',
    SEND_RECEIPT_TOGGLE: 'sendReceipt',
    ADD_NOTE_TEXTAREA: 'addNoteTextarea',
  };

  constructor(
    private _formBuilder: FormBuilder,
    private _translateService: TranslateService,
    private _dateTimeService: DateTimeService
  ) {}

  buildForm(sendReceiptEnabled: boolean): FormGroup {
    return this._formBuilder.group({
      [this.FORM_KEYS.PAYMENT_METHODS_RADIO]: [null],
      [this.FORM_KEYS.CUSTOM_METHOD_RADIO]: [''],
      [this.FORM_KEYS.SEND_RECEIPT_TOGGLE]: [sendReceiptEnabled],
      [this.FORM_KEYS.ADD_NOTE_TEXTAREA]: [null],
    });
  }

  buildDefaultMethods() {
    return [
      this._translateService.instant('PAYMENT_METHOD.CASH'),
      this._translateService.instant('PAYMENT_METHOD.CHECK'),
      this._translateService.instant('PAYMENT_METHOD.BANK_TRANSFER'),
      this._translateService.instant('PAYMENT_METHOD.CREDIT_CARD'),
      this._translateService.instant('PAYMENT_METHOD.PAYPAL'),
    ];
  }

  buildNewRadioField() {
    return this._translateService.instant('ACTION.ADD_PAYMENT_METHOD');
  }

  buildConfirmMarkAsPaidPayload(
    paymentMethod: string,
    description: string,
    isSendReceipt: boolean,
    renderingOptions: IRendererOptions
  ): IMarkAsPaidData {
    return {
      paymentMethod,
      description,
      isSendReceipt,
      renderingOptions,
    };
  }

  buildRenderingOptions(
    paymentsSettings: PaymentsSettings,
    bankTransferPermission: boolean,
    cardPaymentsPermission: boolean,
    paypalPermission: boolean
  ): IRendererOptions {
    return {
      disable_pay_now_link: false,
      card_payments_globally_enabled:
        cardPaymentsPermission && paymentsSettings.cardPaymentsEnabled(),
      paypal_ec_globally_enabled:
        paypalPermission && paymentsSettings.paypalEcEnabled,
      bank_transfer_globally_enabled:
        bankTransferPermission && paymentsSettings.bankTransferEnabled(),
    };
  }

  buildTransactionPayload(
    form: Map<string, any>,
    document: Document
  ): IAddPaymentPayload {
    return {
      amount: document.calculationResult.outstandingBalance || 0,
      method_detail: form.get(this.FORM_KEYS.PAYMENT_METHODS_RADIO) || null,
      description: null,
      datetime: this._dateTimeService.getMidDayUtcOfDate(),
    };
  }

  buildSendReceiptPreview(
    form: Map<string, any>,
    dialogType: string,
    renderingOptions: IRendererOptions,
    document: Document
  ): ISendReceiptPreview {
    return {
      transaction: this.buildTransactionPayload(form, document),
      document,
      dialogType,
      renderingOptions,
    };
  }
}
